import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as customersApi from "./api";
import type { CustomerBody, CustomerFilters } from "./api";

const KEY = "customers";

export function useCustomers(
  filters: CustomerFilters,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, filters],
    queryFn: () => customersApi.listCustomers(filters),
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => customersApi.getCustomer(id as string),
    enabled: id !== null,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [KEY] });
}

export function useCreateCustomer() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: customersApi.createCustomer, onSuccess: invalidate });
}

export function useUpdateCustomer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CustomerBody> }) =>
      customersApi.updateCustomer(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteCustomer() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: customersApi.deleteCustomer, onSuccess: invalidate });
}

export function useAddLocation() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: string;
      body: Parameters<typeof customersApi.addLocation>[1];
    }) => customersApi.addLocation(customerId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteLocation() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: customersApi.deleteLocation, onSuccess: invalidate });
}

export function useAddContact() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: string;
      body: Parameters<typeof customersApi.addContact>[1];
    }) => customersApi.addContact(customerId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: customersApi.deleteContact, onSuccess: invalidate });
}

export function usePortalUsers(customerId: string | null) {
  return useQuery({
    queryKey: [KEY, "portal-users", customerId],
    queryFn: () => customersApi.listPortalUsers(customerId as string),
    enabled: customerId !== null,
  });
}

export function useCreatePortalUser(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      full_name: string;
      email: string;
      phone?: string | null;
      can_view_all_tickets?: boolean;
      location_id?: string | null;
    }) => customersApi.createPortalUser(customerId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "portal-users", customerId] });
    },
  });
}

export function useUpdatePortalUser(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: string;
      is_active?: boolean;
      can_view_all_tickets?: boolean;
      location_id?: string | null;
    }) => customersApi.updatePortalUser(customerId, userId, body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: [KEY, "portal-users", customerId] }),
  });
}

export function useResetPortalUserPassword(customerId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      customersApi.resetPortalUserPassword(customerId, userId),
  });
}

export function useDeletePortalUser(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => customersApi.deletePortalUser(customerId, userId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: [KEY, "portal-users", customerId] }),
  });
}
